<template>
  <v-card class="tarjeta">
    <!-- FOTO: marco con proporcion fija para que el spinner ocupe exactamente
         el mismo alto que la imagen y la tarjeta no pegue un salto al cargar -->
    <div class="marco-foto">
      <div
        v-if="cargandoImagen"
        class="capa-carga"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="28"
          width="3"
        />
      </div>
      <v-img
        v-else
        :src="foto"
        cover
        :aspect-ratio="4 / 3"
        class="foto"
      >
        <template #placeholder>
          <div class="capa-carga" />
        </template>
      </v-img>

      <div
        v-if="!cargandoImagen && !tieneFoto"
        class="sin-foto"
      >
        Sin fotos
      </div>
    </div>

    <!-- DATOS -->
    <div class="cuerpo">
      <template v-if="cargandoDatos">
        <div class="esqueleto esqueleto-titulo" />
        <div class="esqueleto esqueleto-linea" />
        <div class="esqueleto esqueleto-linea corta" />
      </template>

      <template v-else-if="error">
        <div class="titulo">
          Producto #{{ idProducto }}
        </div>
        <div class="aviso-error">
          <v-icon size="16">
            mdi-alert-circle-outline
          </v-icon>
          No se pudieron cargar los datos
        </div>
        <button
          class="reintentar"
          @click="cargarDatos"
        >
          Reintentar
        </button>
      </template>

      <template v-else>
        <div class="titulo">
          {{ producto.product }}
        </div>

        <div class="precio">
          {{ precioFormateado }}
          <span
            v-if="hayPrecio"
            class="moneda"
          >COP</span>
        </div>

        <div
          v-if="producto.category"
          class="categoria"
        >
          {{ producto.category }}
        </div>

        <p
          v-if="producto.description"
          class="descripcion"
        >
          {{ producto.description }}
        </p>

        <div class="vistas">
          <v-icon size="14">
            mdi-eye-outline
          </v-icon>
          {{ producto.views || 0 }} {{ producto.views === 1 ? 'vista' : 'vistas' }}
        </div>
      </template>
    </div>

    <!-- ACCIONES: no dependen de la carga, solo necesitan el id -->
    <div class="acciones">
      <v-btn
        variant="text"
        class="accion accion-principal"
        @click="$emit('editar', idProducto)"
      >
        <v-icon size="17">
          mdi-pencil
        </v-icon>
        Editar
      </v-btn>
      <v-btn
        variant="text"
        class="accion accion-borrar"
        @click="$emit('eliminar', { id: idProducto, nombre: producto ? producto.product : null })"
      >
        <v-icon size="17">
          mdi-delete
        </v-icon>
        Eliminar
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import apisProducts from '@/apis/apisProducts'
import imagenPorDefecto from '@/assets/imagen-none.jpg'

/**
 * Tarjeta de un producto en la pestana de edicion de la empresa.
 *
 * Mismo patron que TarjetaEdicionEmpresa: recibe SOLO el id y carga lo suyo.
 * Primero los datos generales y, cuando llegan, la imagen. Cada tarjeta va a
 * su ritmo, asi que un producto con foto pesada no retrasa a los demas.
 */
export default {
  name: 'TarjetaEdicionProducto',
  props: {
    idProducto: {
      type: Number,
      required: true
    }
  },
  emits: ['editar', 'eliminar'],
  data() {
    return {
      producto: null,
      cargandoDatos: true,
      error: false,
      imagen: null,
      cargandoImagen: true
    }
  },
  computed: {
    tieneFoto() {
      return this.imagen !== null
    },
    foto() {
      return this.imagen || imagenPorDefecto
    },
    hayPrecio() {
      const valor = Number(this.producto?.costs)
      return this.producto?.costs !== null
        && this.producto?.costs !== undefined
        && !isNaN(valor)
    },
    /** 250000 -> "250.000", igual que en la tarjeta publica del producto */
    precioFormateado() {
      return this.hayPrecio ? Number(this.producto.costs).toLocaleString('es-CO') : 'Consultar'
    }
  },
  created() {
    this.cargarDatos()
  },
  methods: {
    cargarDatos() {
      this.cargandoDatos = true
      this.error = false

      return apisProducts.getProductById(this.idProducto)
        .then(producto => {
          this.producto = producto
          this.cargandoDatos = false

          // Encadenada, no en paralelo: la foto arranca cuando el texto ya esta
          return this.cargarImagen()
        })
        .catch(() => {
          this.error = true
          this.cargandoDatos = false
          // Si los datos fallan no se pide la imagen, pero hay que apagar su
          // spinner igual: si no, la tarjeta se queda girando para siempre.
          this.cargandoImagen = false
        })
    },
    cargarImagen() {
      this.cargandoImagen = true

      return apisProducts.ImagesProducts(this.idProducto)
        .then(imagenes => {
          this.imagen = imagenes && imagenes.length > 0 ? imagenes[0].pathBase64 : null
        })
        .catch(() => {
          this.imagen = null
        })
        .finally(() => {
          this.cargandoImagen = false
        })
    }
  }
}
</script>

<style scoped>
.tarjeta {
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s ease;
}
.tarjeta:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.16);
}

/* FOTO */
.marco-foto {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  /* Sin esto el aspect-ratio se lo come el min-height: auto del flex en
     columna y el marco toma el alto natural de la imagen */
  min-height: 0;
  background: #eceff1;
}
.foto {
  width: 100%;
  height: 100%;
}
.capa-carga {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eceff1;
}
.sin-foto {
  position: absolute;
  left: 7px;
  bottom: 7px;
  padding: 2px 7px;
  border-radius: 9px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 0.64rem;
}

/* CUERPO */
.cuerpo {
  flex: 1;
  padding: 10px 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.titulo {
  font-size: 0.92rem;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.precio {
  font-size: 1rem;
  font-weight: 700;
  color: #1976d2;
}
.moneda {
  font-size: 0.66rem;
  font-weight: 600;
  color: #98a2b3;
  margin-left: 2px;
}
.categoria {
  align-self: flex-start;
  padding: 1px 8px;
  border-radius: 9px;
  background: #f2f5f9;
  color: #667085;
  font-size: 0.68rem;
  font-weight: 600;
}
.descripcion {
  margin: 3px 0 0;
  font-size: 0.76rem;
  color: #475467;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.vistas {
  margin-top: auto;
  padding-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  color: #98a2b3;
}

/* ESTADO DE CARGA DEL TEXTO */
.esqueleto {
  border-radius: 6px;
  background: linear-gradient(90deg, #eceff1 25%, #f5f7f8 50%, #eceff1 75%);
  background-size: 200% 100%;
  animation: brillo 1.2s ease-in-out infinite;
}
.esqueleto-titulo {
  height: 15px;
  width: 74%;
  margin-bottom: 5px;
}
.esqueleto-linea {
  height: 10px;
  width: 88%;
}
.esqueleto-linea.corta {
  width: 52%;
}
@keyframes brillo {
  from { background-position: 200% 0; }
  to   { background-position: -200% 0; }
}

/* ERROR */
.aviso-error {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.76rem;
  color: #b42318;
}
.reintentar {
  align-self: flex-start;
  margin-top: 5px;
  padding: 2px 9px;
  border: 1px solid #1976d2;
  border-radius: 6px;
  background: none;
  color: #1976d2;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
}
.reintentar:hover {
  background: #e8f1fb;
}

/* ACCIONES */
.acciones {
  display: flex;
  border-top: 1px solid #eceff1;
}
.accion {
  flex: 1;
  min-width: 0;
  border-radius: 0;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  font-size: 0.78rem;
  gap: 4px;
}
.accion-principal {
  color: #1976d2;
}
.accion-borrar {
  color: #b42318;
}
</style>
